/**
 * ClubsARPicker — Grid of all clubs with their logos. Selecting one opens
 * the AR view (/dashboard/clubes/{id}/ar) of that club with its courts.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Buildings, MagnifyingGlass, Lightning, Cube } from "@phosphor-icons/react";

export default function ClubsARPicker() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    api.get("/clubs")
      .then(({ data }) => setClubs((data || []).filter((c) => !/^TEST_/i.test(c.name || ""))))
      .catch(() => setClubs([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clubs;
    return clubs.filter((c) => [c.name, c.city, c.state, c.country].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [clubs, search]);

  return (
    <div className="space-y-5" data-testid="clubs-ar-picker">
      {/* Hero */}
      <div className="card-tactical relative overflow-hidden p-6" style={{ borderColor: "rgba(0,160,255,0.4)" }}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(0,160,255,0.25) 0%, transparent 70%)" }} />
          <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(204,255,0,0.20) 0%, transparent 70%)" }} />
        </div>
        <div className="relative flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#00A0FF] font-bold mb-1">Clubes en Realidad Aumentada</p>
            <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-none">Elige tu Club</h1>
            <p className="text-xs text-gray-400 mt-2 max-w-xl">Selecciona un club para ver su <strong className="text-[#CCFF00]">vista AR 3D</strong> con todas sus canchas, estado en vivo, ocupación y el logo del club en el centro del complejo.</p>
          </div>
          <Badge variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px] tracking-widest shrink-0">
            <Lightning size={11} weight="fill" className="mr-1" /> {clubs.length} clubes
          </Badge>
        </div>
      </div>

      {/* Search */}
      <div className="card-tactical p-3 flex items-center gap-2 flex-wrap" data-testid="clubs-ar-search-bar">
        <div className="relative flex-1 min-w-[260px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar club por nombre, ciudad o estado…"
            className="bg-[#0A0A0A] border-white/10 text-white pl-9 h-10"
            data-testid="clubs-ar-search-input"
          />
          {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs">✕</button>}
        </div>
        <span className="text-[10px] uppercase tracking-widest text-gray-500">{filtered.length} de {clubs.length}</span>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="card-tactical p-12 text-center" data-testid="clubs-ar-empty">
          <Buildings size={48} weight="duotone" className="mx-auto text-[#CCFF00]/40" />
          <p className="text-gray-400 mt-4">{search ? "Sin resultados con ese término" : "Aún no hay clubes registrados"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="clubs-ar-grid">
          {filtered.map((c) => {
            const courtsCount = c.court_names?.length || c.courts_count || 0;
            const logoUrl = c.logo_path ? `${apiBase}/api/clubs/${c.id}/logo` : null;
            return (
              <Link
                key={c.id}
                to={`/dashboard/clubes/${c.id}/ar`}
                className="card-tactical p-4 group hover:border-[#00A0FF]/60 transition-all relative overflow-hidden flex flex-col items-center text-center"
                data-testid={`clubs-ar-card-${c.id}`}
              >
                <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl opacity-0 group-hover:opacity-60 transition-opacity pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,160,255,0.5) 0%, transparent 70%)" }} aria-hidden />

                {/* Logo */}
                <div className="relative w-24 h-24 flex items-center justify-center bg-white border-2 border-white/10 group-hover:border-[#00A0FF]/40 mb-3 overflow-hidden">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={c.name}
                      className="w-full h-full object-contain"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                      data-testid={`clubs-ar-logo-${c.id}`}
                    />
                  ) : (
                    <Buildings size={32} weight="duotone" className="text-gray-400" />
                  )}
                </div>

                {/* Name */}
                <h3 className="font-['Barlow_Condensed'] font-black text-base uppercase tracking-tight text-white group-hover:text-[#00A0FF] line-clamp-2 leading-tight" data-testid={`clubs-ar-name-${c.id}`}>
                  {c.name}
                </h3>
                {(c.city || c.state) && (
                  <p className="text-[10px] text-gray-500 mt-0.5 truncate w-full">{[c.city, c.state].filter(Boolean).join(", ")}</p>
                )}

                {/* Stats */}
                <div className="flex items-center gap-2 mt-2 text-[9px] uppercase tracking-widest">
                  <span className="bg-[#CCFF00]/15 text-[#CCFF00] border border-[#CCFF00]/30 px-1.5 py-0.5 font-bold">{courtsCount} canchas</span>
                  <span className="bg-blue-500/15 text-blue-300 border border-blue-500/30 px-1.5 py-0.5 font-bold flex items-center gap-1">
                    <Cube size={9} weight="duotone" /> AR
                  </span>
                </div>

                {/* CTA */}
                <span className="mt-3 text-[10px] uppercase tracking-widest text-gray-500 group-hover:text-[#00A0FF] font-bold">
                  Ver en AR →
                </span>
              </Link>
            );
          })}
        </div>
      )}

      {/* Tip */}
      <div className="card-tactical p-4 flex items-start gap-3" data-testid="clubs-ar-tip">
        <Cube size={28} weight="duotone" className="text-[#00A0FF] shrink-0" />
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#00A0FF]">Tip</p>
          <p className="text-xs text-gray-400">
            ¿Eres directivo? Ve a la página de tu club y pulsa <strong className="text-[#CCFF00]">"Subir logo"</strong> para que aparezca aquí y en el centro de la vista AR de tu complejo.
          </p>
        </div>
      </div>
    </div>
  );
}
